/**
 * Marketplace Map — präzise DACH-Karte (DE / AT / CH).
 *
 * Die Länderkonturen kommen aus echten Geo-Daten (Natural Earth via
 * datasets/geo-countries) → projiziert, vereinfacht und in `_dachGeo.ts`
 * abgelegt. Jedes Land wird als eigener Pfad mit leuchtendem Gradient-
 * Stroke gezeichnet (Türkis → Violett → Magenta) und animiert per
 * stroke-dashoffset draw-in.
 *
 * Visual:
 *  - 1:1 quadratisches Map-Rahmen (kein Aspekt-Letterboxing, viewBox 0..1000).
 *  - Drei Country-Outlines, je nach Land in eigener Akzent-Range, mit
 *    weichem Glow per filter:drop-shadow.
 *  - Country-Labels (Deutschland, Schweiz, Österreich) als SVG-Text mittig
 *    im jeweiligen Land — passend zur Referenz vom Brand-Mood-Board.
 *  - Optional Drop-Pins für Städte (HTML-Overlay, %-Positionierung anhand
 *    derselben viewBox).
 *  - Backdrop: `allyver` (Brand-Glows).
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { splitItem, sceneHeader } from "./_mockHelpers.js";
import { DACH_PATHS, DACH_LABELS, DACH_CITY_POS, DACH_VIEWBOX } from "./_dachGeo.js";

/** Approx. perimeter (in viewBox units) of each country path — used as
 *  stroke-dasharray for the draw-in animation. Slightly oversized so the
 *  dash always fully covers the outline. */
const DACH_PATH_LEN = {
  DEU: 4400,
  AUT: 1900,
  CHE: 1400,
} as const;

export const marketplaceMap: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const cyan = "#22d3ee";
  const violet = "#a855f7";
  const pink = "#ec4899";
  const headline = inputs.text ?? "";
  const sub = inputs.subtext ?? "";
  const items = (inputs.items?.length ? inputs.items : []).slice(0, 14);

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const isVertical = ctx.format === "9:16";

  const wrapId = `${clipPrefix}-wrap`;
  const mapId = `${clipPrefix}-map`;
  const deuId = `${clipPrefix}-deu`;
  const autId = `${clipPrefix}-aut`;
  const cheId = `${clipPrefix}-che`;
  const lblIds = { DEU: `${clipPrefix}-lbl-deu`, AUT: `${clipPrefix}-lbl-aut`, CHE: `${clipPrefix}-lbl-che` };
  const pinIds = items.map((_, i) => `${clipPrefix}-pin-${i}`);
  const haloIds = items.map((_, i) => `${clipPrefix}-halo-${i}`);
  const cityLblIds = items.map((_, i) => `${clipPrefix}-clbl-${i}`);
  const connectorIds = items.map((_, i) => `${clipPrefix}-cn-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const header = sceneHeader({ clipPrefix, accent, eyebrow: sub, title: headline, titlePx: lg * 0.8, subPx: sm * 0.95 });

  // Square so the 0..1000 viewBox maps cleanly to percentages for the
  // HTML pin overlay (xPercent = x / 10).
  const mapSize = isVertical ? 820 : 980;

  // Per-pin layout for the HTML overlay.
  const layout = items.map((raw, i) => {
    const [city, sizeStr] = splitItem(raw);
    const key = (city ?? "").toLowerCase().replace(/[^a-zäöü]/g, "");
    const vb = DACH_CITY_POS[key] ?? [500 + ((i * 53) % 200) - 100, 500 + ((i * 71) % 200) - 100];
    const size = Math.max(1, Math.min(9, parseInt(sizeStr ?? "5", 10)));
    const dotSize = 10 + size * 1.5;
    const haloSize = dotSize * 3.6;
    const side = i % 2 === 0 ? -1 : 1;
    const labelDx = side * (18 + (i % 3) * 6);
    const labelDy = -(haloSize / 2 + 28 + ((Math.floor(i / 2)) % 2) * 12);
    // viewBox units → percent of the square container.
    return { city: city ?? "", xPct: vb[0] / 10, yPct: vb[1] / 10, size, dotSize, haloSize, labelDx, labelDy };
  });

  // Subtle cartographer dotted grid behind the silhouette.
  const grid = `<div style="position:absolute;inset:0;background-image:radial-gradient(${withAlpha(themeColors.fg, 0.08)} 1px, transparent 1px);background-size:28px 28px;opacity:0.55;mix-blend-mode:screen;pointer-events:none;"></div>`;

  // Soft country-tinted glows sitting *under* the outlines so each region
  // picks up its own brand colour without obscuring the silhouette.
  const tints = `
    <div style="position:absolute;left:${DACH_LABELS.DEU.x / 10}%;top:${DACH_LABELS.DEU.y / 10}%;width:48%;height:48%;border-radius:50%;background:radial-gradient(closest-side, ${withAlpha(cyan, 0.22)} 0%, transparent 70%);transform:translate(-50%,-50%);filter:blur(28px);pointer-events:none;"></div>
    <div style="position:absolute;left:${DACH_LABELS.AUT.x / 10}%;top:${DACH_LABELS.AUT.y / 10}%;width:32%;height:24%;border-radius:50%;background:radial-gradient(closest-side, ${withAlpha(pink, 0.22)} 0%, transparent 70%);transform:translate(-50%,-50%);filter:blur(24px);pointer-events:none;"></div>
    <div style="position:absolute;left:${DACH_LABELS.CHE.x / 10}%;top:${DACH_LABELS.CHE.y / 10}%;width:22%;height:18%;border-radius:50%;background:radial-gradient(closest-side, ${withAlpha(violet, 0.22)} 0%, transparent 70%);transform:translate(-50%,-50%);filter:blur(22px);pointer-events:none;"></div>
  `;

  // Country label font size — scales with map size for legibility.
  const lblPx = Math.round(mapSize * 0.038);

  const dachSvg = `<svg viewBox="${DACH_VIEWBOX}" preserveAspectRatio="xMidYMid meet" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible;">
    <defs>
      <linearGradient id="${clipPrefix}-grad-deu" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="${cyan}" />
        <stop offset="55%" stop-color="#60a5fa" />
        <stop offset="100%" stop-color="${violet}" />
      </linearGradient>
      <linearGradient id="${clipPrefix}-grad-aut" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stop-color="${violet}" />
        <stop offset="100%" stop-color="${pink}" />
      </linearGradient>
      <linearGradient id="${clipPrefix}-grad-che" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#60a5fa" />
        <stop offset="100%" stop-color="${violet}" />
      </linearGradient>
      <filter id="${clipPrefix}-glow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <g filter="url(#${clipPrefix}-glow)">
      <path id="${deuId}" d="${DACH_PATHS.DEU}"
            fill="${withAlpha(cyan, 0.04)}"
            stroke="url(#${clipPrefix}-grad-deu)"
            stroke-width="3"
            stroke-linecap="round" stroke-linejoin="round"
            stroke-dasharray="${DACH_PATH_LEN.DEU}"
            stroke-dashoffset="${DACH_PATH_LEN.DEU}" />
      <path id="${autId}" d="${DACH_PATHS.AUT}"
            fill="${withAlpha(violet, 0.04)}"
            stroke="url(#${clipPrefix}-grad-aut)"
            stroke-width="3"
            stroke-linecap="round" stroke-linejoin="round"
            stroke-dasharray="${DACH_PATH_LEN.AUT}"
            stroke-dashoffset="${DACH_PATH_LEN.AUT}" />
      <path id="${cheId}" d="${DACH_PATHS.CHE}"
            fill="${withAlpha(violet, 0.04)}"
            stroke="url(#${clipPrefix}-grad-che)"
            stroke-width="3"
            stroke-linecap="round" stroke-linejoin="round"
            stroke-dasharray="${DACH_PATH_LEN.CHE}"
            stroke-dashoffset="${DACH_PATH_LEN.CHE}" />
    </g>

    <text id="${lblIds.DEU}" x="${DACH_LABELS.DEU.x}" y="${DACH_LABELS.DEU.y}"
          text-anchor="middle" dominant-baseline="middle"
          font-family="Outfit,sans-serif" font-weight="${weights.semibold}"
          font-size="${lblPx}" fill="${themeColors.fg}"
          style="letter-spacing:0.5px;">Deutschland</text>
    <text id="${lblIds.CHE}" x="${DACH_LABELS.CHE.x}" y="${DACH_LABELS.CHE.y}"
          text-anchor="middle" dominant-baseline="middle"
          font-family="Outfit,sans-serif" font-weight="${weights.semibold}"
          font-size="${lblPx}" fill="${themeColors.fg}"
          style="letter-spacing:0.5px;">Schweiz</text>
    <text id="${lblIds.AUT}" x="${DACH_LABELS.AUT.x}" y="${DACH_LABELS.AUT.y}"
          text-anchor="middle" dominant-baseline="middle"
          font-family="Outfit,sans-serif" font-weight="${weights.semibold}"
          font-size="${lblPx}" fill="${themeColors.fg}"
          style="letter-spacing:0.5px;">Österreich</text>
  </svg>`;

  const pinGlyph = (color: string, w: number) => `
    <svg viewBox="0 0 24 24" width="${w}" height="${w}" style="display:block;filter:drop-shadow(0 4px 10px rgba(0,0,0,0.6));">
      <path d="M12 1.5c-4.4 0-8 3.6-8 8 0 6 8 13 8 13s8-7 8-13c0-4.4-3.6-8-8-8z"
            fill="${color}" stroke="${withAlpha(themeColors.fg, 0.55)}" stroke-width="0.7" />
      <circle cx="12" cy="9.5" r="3" fill="${themeColors.bg}" />
    </svg>`;

  const pinsHtml = layout.map((l, i) => {
    const colorPick = i % 3 === 0 ? cyan : i % 3 === 1 ? violet : pink;
    return `
      <div id="${haloIds[i]}" style="position:absolute;left:${l.xPct}%;top:${l.yPct}%;transform:translate(-50%,-50%);width:${l.haloSize}px;height:${l.haloSize}px;border-radius:9999px;background:radial-gradient(circle, ${withAlpha(colorPick, 0.55)} 0%, transparent 65%);pointer-events:none;z-index:2;"></div>
      <div id="${pinIds[i]}" style="position:absolute;left:${l.xPct}%;top:${l.yPct}%;transform:translate(-50%,-100%);z-index:3;">${pinGlyph(colorPick, l.dotSize * 2.2)}</div>
      <svg id="${connectorIds[i]}" style="position:absolute;left:${l.xPct}%;top:${l.yPct}%;width:1px;height:1px;overflow:visible;z-index:3;pointer-events:none;" viewBox="-100 -100 200 200">
        <line x1="0" y1="-${l.dotSize * 1.8}" x2="${l.labelDx}" y2="${l.labelDy + 14}"
              stroke="${withAlpha(colorPick, 0.6)}" stroke-width="1.2" stroke-dasharray="2,3" />
      </svg>
      <div id="${cityLblIds[i]}" style="position:absolute;left:${l.xPct}%;top:${l.yPct}%;transform:translate(calc(-50% + ${l.labelDx}px), calc(${l.labelDy}px));padding:5px 11px;border-radius:9999px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.bg, 0.9)} 100%);border:1px solid ${withAlpha(colorPick, 0.55)};color:${themeColors.fg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.52}px;letter-spacing:0.6px;white-space:nowrap;z-index:4;box-shadow:0 6px 18px rgba(0,0,0,0.5), 0 0 16px ${withAlpha(colorPick, 0.35)};backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);">${escapeHtml(l.city)}</div>`;
  }).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:24px;padding:64px;">
    ${header.html}
    <div id="${mapId}" style="position:relative;width:${mapSize}px;height:${mapSize}px;max-width:100%;max-height:100%;border:1.5px solid ${withAlpha(themeColors.fg, 0.1)};border-radius:36px;background:radial-gradient(ellipse at 50% 50%, ${withAlpha(accent, 0.1)} 0%, ${withAlpha(themeColors.bg, 0.75)} 70%);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);overflow:hidden;box-shadow:inset 0 1px 0 ${withAlpha(themeColors.fg, 0.08)}, 0 30px 70px rgba(0,0,0,0.45);">
      ${grid}
      ${tints}
      ${dachSvg}
      ${pinsHtml}
    </div>
  </div>`;

  const drawDur = 1.4;

  const init = [
    sub ? gsapSetLine(`#${header.ids.eyebrow}`, { opacity: 0, y: 12 }) : "",
    headline ? gsapSetLine(`#${header.ids.title}`, { opacity: 0, y: 16 }) : "",
    gsapSetLine(`#${mapId}`, { opacity: 0, scale: 0.96 }),
    gsapSetLine(`#${lblIds.DEU}`, { opacity: 0 }),
    gsapSetLine(`#${lblIds.AUT}`, { opacity: 0 }),
    gsapSetLine(`#${lblIds.CHE}`, { opacity: 0 }),
    ...pinIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: -16, scale: 0.4 })),
    ...haloIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.4 })),
    ...cityLblIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: -8 })),
    ...connectorIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (sub) tweens.push(t.to(`#${header.ids.eyebrow}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.0));
  if (headline) tweens.push(t.to(`#${header.ids.title}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.15));
  tweens.push(t.to(`#${mapId}`, { opacity: 1, scale: 1, duration: 0.8, ease: "raw:\"power3.out\"" }, 0.25));

  // Draw the three country outlines (DE first — biggest path — then CH/AT
  // in parallel a beat later so the eye reads "DACH" as one motion).
  tweens.push(t.to(`#${deuId}`, { attr: { "stroke-dashoffset": 0 }, duration: drawDur, ease: "raw:\"power2.out\"" }, 0.45));
  tweens.push(t.to(`#${cheId}`, { attr: { "stroke-dashoffset": 0 }, duration: drawDur * 0.85, ease: "raw:\"power2.out\"" }, 0.85));
  tweens.push(t.to(`#${autId}`, { attr: { "stroke-dashoffset": 0 }, duration: drawDur * 0.85, ease: "raw:\"power2.out\"" }, 0.95));

  // Country names fade in once the outlines have mostly drawn.
  const lblAt = 0.45 + drawDur * 0.75;
  tweens.push(t.to(`#${lblIds.DEU}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, lblAt));
  tweens.push(t.to(`#${lblIds.CHE}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, lblAt + 0.1));
  tweens.push(t.to(`#${lblIds.AUT}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, lblAt + 0.2));

  // City pins (optional) — stagger after the outlines finished.
  if (items.length > 0) {
    const pinsStart = 0.45 + drawDur + 0.15;
    const stagger = Math.max(0.12, (clipDurationSec - pinsStart - 0.6) / Math.max(items.length, 1));
    pinIds.forEach((id, i) => {
      const at = pinsStart + i * stagger;
      tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(2.2)\"" }, at));
      tweens.push(t.to(`#${haloIds[i]}`, { opacity: 1, scale: 1, duration: 0.55, ease: "raw:\"power2.out\"" }, at));
      tweens.push(t.to(`#${haloIds[i]}`, { scale: 1.2, opacity: 0.65, duration: 1.4, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, at + 0.3));
      tweens.push(t.to(`#${connectorIds[i]}`, { opacity: 1, duration: 0.4, ease: "raw:\"power2.out\"" }, at + 0.15));
      tweens.push(t.to(`#${cityLblIds[i]}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, at + 0.25));
    });
  }
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
